import {useEffect, useRef} from 'react'
import type {GenericContent} from '../../../../../../brand/lib/types/contentful'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'

import {Button, Heading} from '@primer/react-brand/esm'
import {Label} from '../../_shared/Label/Label'

import Styles from './DeepDive.module.css'

type Props = {
  content: GenericContent
  location: string
}

export function DeepDive(props: Props) {
  const {content, location} = props
  const {heading, label} = content.fields
  const links = (content.fields.content as GenericContent[] | undefined) ?? []

  const title = heading
  const eyebrow = label?.fields.text

  // Animation
  const wrapperRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const linkRefsArray = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    const elements: AnimationElement[] = []
    if (titleRef.current) elements.push({element: titleRef.current})
    for (const linkRef of linkRefsArray.current.filter(Boolean)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      elements.push({element: linkRef!})
    }
    if (wrapperRef.current) {
      return animateFadeIn(elements, wrapperRef.current)
    }
  }, [])

  return (
    <div className={Styles.DeepDive} ref={wrapperRef}>
      <div className={Styles.Wrapper}>
        {eyebrow && <Label className={Styles.Eyebrow} text={eyebrow} />}

        {title && (
          <div ref={titleRef}>
            <Heading className={Styles.Title} data-heading="1" as="h1">
              {title}
            </Heading>
          </div>
        )}
      </div>

      <div className={Styles.Grid}>
        {links?.map((item, itemIndex) => {
          const link = item.fields.links?.[0]?.fields

          return link ? (
            <Button
              key={item.sys.id}
              className={Styles.Button}
              as="a"
              variant="subtle"
              href={link.href}
              {...getAnalyticsEvent({
                action: link.text,
                tag: 'a',
                context: 'link',
                location,
              })}
              ref={el => {
                linkRefsArray.current[itemIndex] = el
              }}
            >
              {link.text}

              <svg role="presentation" xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24">
                <path d="M22.9458 5.54128L19.0019 1.5974C14.6582 5.029 10.0703 4.0775 5.98868 0.532802L1.62054 4.90094C4.29201 7.57241 7.88817 9.23515 11.1145 8.84296L0.64379 19.3137L5.24499 23.9149L15.7157 13.4442C15.3236 16.6705 16.9863 20.2667 19.6578 22.9382L24.0259 18.57C20.533 14.5401 19.6436 10.0143 22.9458 5.54128Z" />
              </svg>
            </Button>
          ) : null
        })}
      </div>
    </div>
  )
}
